

const DB_NAME = "scano-technician-offline";
const DB_VERSION = 1;
const STORE = "queue";

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) {
      reject(new Error("IndexedDB is not available in this browser."));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "clientActionId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function withStore(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const result = fn(store);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
  });
}

function isNetworkError(error) {
  // axios: a real server response (even a 4xx/5xx) means the request reached
  // the backend and failed for a reason retrying won't fix — surface it
  // normally. No response at all is what "no signal at the clinic" looks
  // like, so that's the case worth queuing.
  return !error?.response;
}

function newClientActionId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Add a queued action to IndexedDB, oldest-first ordering via createdAt. */
async function enqueue(record) {
  await withStore("readwrite", (store) => store.put(record));
}

async function removeFromQueue(clientActionId) {
  await withStore("readwrite", (store) => store.delete(clientActionId));
}

async function listQueued() {
  return withStore("readonly", (store) => {
    const out = [];
    return new Promise((resolve, reject) => {
      const cursorReq = store.openCursor();
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor) {
          out.push(cursor.value);
          cursor.continue();
        } else {
          resolve(out.sort((a, b) => a.createdAt - b.createdAt));
        }
      };
      cursorReq.onerror = () => reject(cursorReq.error);
    });
  });
}

/**
 * Attempts `sendFn(payloadWithSyncFields)` immediately. If it fails because
 * there's no connection (or the browser already knows it's offline), the
 * action is queued instead and this resolves with `{ queued: true }` rather
 * than throwing, so the screen can show "saved, will sync" instead of an
 * error. A real server-side rejection (bad input, 400, etc.) is NOT queued —
 * it's thrown as-is so the user can fix it now.
 *
 * @param {string} type - a key in DISPATCH (see api.service.js) used to
 *   replay this action later.
 * @param {object} payload - plain JSON fields + File/Blob fields (photos,
 *   signature) as applicable to this action type.
 * @param {function} sendFn - (payloadWithSyncFields) => Promise<response>
 */
async function queueOrSend(type, payload, sendFn) {
  const clientActionId = newClientActionId();
  const occurredAt = new Date().toISOString();
  const fullPayload = { ...payload, clientActionId, occurredAt };

  if (navigator.onLine) {
    try {
      const res = await sendFn(fullPayload);
      return res;
    } catch (error) {
      if (!isNetworkError(error)) throw error;
      // Fall through to queueing below.
    }
  }

  await enqueue({ clientActionId, type, payload: fullPayload, createdAt: Date.now() });
  return { success: true, queued: true, data: null };
}

let flushing = false;

/**
 * Replays every queued action in the order it was recorded. `dispatch` maps
 * a queued action's `type` to the apiService function that actually sends
 * it. Stops at the first still-offline failure (keeps the rest queued for
 * the next attempt) but drops an action that the server outright rejects,
 * since retrying an invalid request forever would just wedge the queue.
 */
async function flushQueue(dispatch) {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    const queued = await listQueued();
    for (const record of queued) {
      const sendFn = dispatch[record.type];
      if (!sendFn) {
        // Unknown type (e.g. an old app version queued something this build
        // doesn't recognize) — drop it rather than looping on it forever.
        await removeFromQueue(record.clientActionId);
        continue;
      }
      try {
        await sendFn(record.payload);
        await removeFromQueue(record.clientActionId);
      } catch (error) {
        if (isNetworkError(error)) {
          // Signal dropped again mid-sync — stop here, keep the rest queued.
          break;
        }
        // Server rejected it outright; nothing left to do with a stale
        // queued action but drop it.
        console.error(`Dropping queued offline action (${record.type}) rejected by server:`, error);
        await removeFromQueue(record.clientActionId);
      }
    }
  } finally {
    flushing = false;
  }
}

async function queuedCount() {
  const queued = await listQueued();
  return queued.length;
}

/** Call once on app start. Flushes immediately if already online, and again
 * every time the browser regains connectivity. */
function startAutoSync(dispatch) {
  flushQueue(dispatch);
  window.addEventListener("online", () => flushQueue(dispatch));
}

const offlineQueue = { queueOrSend, flushQueue, startAutoSync, queuedCount };
export default offlineQueue;
